/**
 * Connection Validator for React Flow
 *
 * Ported from magic-canvas ConnectionValidator + DataTypeCompatibility.
 * Validates edge connections with:
 * - Self-connection prevention
 * - Duplicate connection prevention
 * - Data type compatibility matrix
 * - Max connections per handle
 * - Optional cycle detection (BFS)
 */

import type { Connection, Node, Edge } from '@xyflow/react'
import type { HandleMeta } from '@/lib/canvas/types'
import { resolveHandlesForNode } from '@/lib/canvas/configs/handles'

/* ---- Data Type Compatibility Matrix ---- */

const COMPATIBILITY_MATRIX: Record<string, string[]> = {
  any: ['any', 'text', 'number', 'image', 'video', 'code', 'audio', 'mask', 'file', '3d', 'boolean', 'lora'],
  text: ['any', 'text'],
  number: ['any', 'number', 'string'],
  string: ['any', 'string', 'text'],
  image: ['any', 'image'],
  video: ['any', 'video'],
  code: ['any', 'code'],
  audio: ['any', 'audio'],
  mask: ['any', 'mask', 'image'],  // masks are image-compatible
  file: ['any', 'file'],
  '3d': ['any', '3d'],
  boolean: ['any', 'boolean'],
  lora: ['any', 'lora'],
}

export function isDataTypeCompatible(sourceType: string, targetType: string): boolean {
  const source = sourceType.toLowerCase()
  const target = targetType.toLowerCase()

  if (source === 'any' || target === 'any') return true

  const compatible = COMPATIBILITY_MATRIX[source]
  if (!compatible) return source === target

  return compatible.includes(target)
}

/* ---- Handle metadata helpers ---- */

function getHandleMeta(node: Node, handleId: string | null): HandleMeta | undefined {
  if (!handleId) return undefined
  const handles = resolveHandlesForNode(node)
  return handles.find((h) => h.id === handleId)
}

/* ---- Cycle detection (BFS) ---- */

function wouldCreateCycle(
  sourceId: string,
  targetId: string,
  edges: Edge[],
): boolean {
  // Check if there's a path from target → source (would create a cycle)
  const visited = new Set<string>()
  const queue = [targetId]

  while (queue.length > 0) {
    const current = queue.shift()!
    if (current === sourceId) return true
    if (visited.has(current)) continue
    visited.add(current)

    for (const edge of edges) {
      if (edge.target === current && !visited.has(edge.source)) {
        queue.push(edge.source)
      }
    }
  }

  return false
}

/* ---- Options ---- */

export interface ConnectionValidatorOptions {
  preventSelfConnection?: boolean
  preventDuplicates?: boolean
  preventCycles?: boolean
  maxInputConnections?: number
  maxOutputConnections?: number
  checkDataTypes?: boolean
}

const DEFAULT_OPTIONS: Required<ConnectionValidatorOptions> = {
  preventSelfConnection: true,
  preventDuplicates: true,
  preventCycles: false,
  maxInputConnections: 1,
  maxOutputConnections: Infinity,
  checkDataTypes: true,
}

/* ---- Main validator ---- */

export function isValidConnection(
  connection: Connection,
  nodes: Node[],
  edges: Edge[],
  options?: ConnectionValidatorOptions,
): boolean {
  const opts = { ...DEFAULT_OPTIONS, ...options }

  const { source, target, sourceHandle, targetHandle } = connection

  if (!source || !target) return false

  // 1. Self-connection
  if (opts.preventSelfConnection && source === target) return false

  // 2. Duplicate connection
  if (opts.preventDuplicates) {
    const isDuplicate = edges.some(
      (e) =>
        (e.source === source && e.target === target && e.sourceHandle === sourceHandle && e.targetHandle === targetHandle) ||
        (e.source === target && e.target === source && e.sourceHandle === targetHandle && e.targetHandle === sourceHandle),
    )
    if (isDuplicate) return false
  }

  // Get nodes
  const sourceNode = nodes.find((n) => n.id === source)
  const targetNode = nodes.find((n) => n.id === target)
  if (!sourceNode || !targetNode) return false

  // 3. Locked handle check
  // Only block connections TO locked input handles (e.g., parent-input on file nodes).
  // Locked output handles (e.g., generated-output) should still allow outgoing connections
  // since they auto-wire to generated child nodes but may also be manually connected.
  const sourceMeta = getHandleMeta(sourceNode, sourceHandle)
  const targetMeta = getHandleMeta(targetNode, targetHandle)

  if (targetMeta?.locked) return false

  // 4. Data type compatibility (fail-closed: reject if handle metadata can't be resolved)
  if (opts.checkDataTypes) {
    if (!sourceMeta || !targetMeta) return false
    const sourceType = sourceMeta.dataType || 'any'
    const targetType = targetMeta.dataType || 'any'
    if (!isDataTypeCompatible(sourceType, targetType)) return false
  }

  // 5. Max connections per input handle
  if (targetHandle) {
    const maxInput = targetMeta?.maxConnections ?? opts.maxInputConnections
    const currentInputCount = edges.filter(
      (e) => e.target === target && e.targetHandle === targetHandle,
    ).length
    if (currentInputCount >= maxInput) return false
  }

  // 6. Max connections per output handle
  if (sourceHandle && opts.maxOutputConnections !== Infinity) {
    const maxOutput = sourceMeta?.maxConnections ?? opts.maxOutputConnections
    const currentOutputCount = edges.filter(
      (e) => e.source === source && e.sourceHandle === sourceHandle,
    ).length
    if (currentOutputCount >= maxOutput) return false
  }

  // 7. Cycle prevention
  if (opts.preventCycles) {
    if (wouldCreateCycle(source, target, edges)) return false
  }

  return true
}
